import React from "react";
import { Link as GatsbyLink } from "gatsby";
import { Link as IntlLink } from "gatsby-plugin-intl";
import styled from "styled-components";
import Icon from "./Icon";
import { languageMetadata } from "../utils/translations";
import { trackCustomEvent } from "../utils/matomo";
var HASH_PATTERN = /^#.*/; // const DOMAIN_PATTERN = /^(?:https?:)?[/]{2,}([^/]+)/
// const INTERNAL_PATTERN = /^\/(?!\/)/
// const FILE_PATTERN = /.*[/](.+\.[^/]+?)([/].*?)?([#?].*)?$/

var isHashLink = function isHashLink(to) {
  return HASH_PATTERN.test(to);
};

var ExternalLink = styled.a.withConfig({
  displayName: "Link__ExternalLink",
  componentId: "e3riao-0"
})(["&:after{margin-left:0.125em;margin-right:0.3em;display:inline;content:\"\u2197\";transition:all 0.1s ease-in-out;font-style:normal;}&:hover{&:after{transform:translate(0.15em,-0.2em);}}"]);
var InternalLink = styled(IntlLink).withConfig({
  displayName: "Link__InternalLink",
  componentId: "e3riao-1"
})([".is-glossary{white-space:nowrap;}&.active{color:", ";}&:hover{svg{fill:", ";transition:transform 0.1s;transform:scale(1.2);}}"], function (props) {
  return props.theme.colors.primary;
}, function (props) {
  return props.theme.colors.primary;
});
var GlossaryIcon = styled(Icon).withConfig({
  displayName: "Link__GlossaryIcon",
  componentId: "e3riao-2"
})(["margin:0 0.25rem 0 0.35rem;fill:", ";text-decoration:underline;&:hover{transition:transform 0.1s;transform:scale(1.2);}"], function (props) {
  return props.theme.colors.primary400;
});

var Link = function Link(_ref) {
  var to = _ref.to,
      href = _ref.href,
      children = _ref.children,
      _ref$hideArrow = _ref.hideArrow,
      hideArrow = _ref$hideArrow === void 0 ? false : _ref$hideArrow,
      className = _ref.className,
      _ref$isPartiallyActiv = _ref.isPartiallyActive,
      isPartiallyActive = _ref$isPartiallyActiv === void 0 ? true : _ref$isPartiallyActiv; // markdown pages pass `href`, not `to`

  to = to || href;
  var isExternal = to.includes("http") || to.includes("mailto:");
  var isHash = isHashLink(to);
  var isGlossary = to.includes("glossary");
  var isStatic = to.includes("static"); // Must use <a> tags for anchor links
  // Otherwise <Link> functionality will navigate to homepage
  // See https://github.com/gatsbyjs/gatsby/issues/21909

  if (isHash) {
    return /*#__PURE__*/React.createElement("a", {
      className: className,
      href: to
    }, children);
  } // Links to static image assets must use <a> to avoid
  // <Link> redirection. Opens in separate window.


  if (isStatic) {
    return /*#__PURE__*/React.createElement("a", {
      className: className,
      href: to,
      target: "_blank",
      rel: "noopener noreferrer"
    }, children);
  }

  var eventOptions = {
    eventCategory: "External link",
    eventAction: "Clicked",
    eventName: to
  };

  if (isExternal) {
    return hideArrow ? /*#__PURE__*/React.createElement("a", {
      className: className,
      href: to,
      target: "_blank",
      rel: "noopener noreferrer",
      onClick: function onClick() {
        return trackCustomEvent(eventOptions);
      }
    }, children) : /*#__PURE__*/React.createElement(ExternalLink, {
      className: className,
      href: to,
      target: "_blank",
      rel: "noopener noreferrer",
      onClick: function onClick() {
        return trackCustomEvent(eventOptions);
      }
    }, children);
  } // If lang path has been explicitly set, use `gatsby` Link


  var langPath = to.split("/")[1];

  if (Object.keys(languageMetadata).includes(langPath)) {
    return /*#__PURE__*/React.createElement(GatsbyLink, {
      className: className,
      to: to,
      activeClassName: "active",
      partiallyActive: isPartiallyActive
    }, children);
  } // Use `gatsby-plugin-intl` Link (which prepends lang path)


  return /*#__PURE__*/React.createElement(InternalLink, {
    className: isGlossary ? "is-glossary " + className : className,
    to: to,
    activeClassName: "active",
    partiallyActive: isPartiallyActive
  }, children, isGlossary && /*#__PURE__*/React.createElement(GlossaryIcon, {
    "aria-label": "See definition",
    size: "12px",
    name: "glossary"
  }));
};

export default Link;